import { computed, type Ref } from 'vue'
import type { Light } from '@/stores/lights'
import { hueStateToHSV, hsvToXY, ctToXY, type XYCoord } from '@/lib/colorConversion'
import { useSceneLightConfigsStore, type ResolvedSceneLightConfig } from '@/stores/sceneLightConfigs'
import { useScenesStore } from '@/stores/scenes'

// Proximity threshold for grouping (0-1 normalized coordinates)
// Lights within this distance are considered "stacked" at the same position
const PROXIMITY_THRESHOLD = 0.05

export interface LightWithPosition {
  light: Light
  position: XYCoord
  isInherited: boolean
  inheritedFrom: string
}

export interface StackedGroup {
  id: string // Generated ID based on position
  lights: LightWithPosition[]
  position: XYCoord
}

export interface DragOverride {
  lightIds: Set<string>
  targetCircle: 'hue' | 'white' | null
}

export function useLightPositioning(
  lights: Ref<Light[]>,
  currentSceneId: Ref<string | null>,
  dragOverride?: Ref<DragOverride>
) {
  const sceneLightConfigsStore = useSceneLightConfigsStore()
  const scenesStore = useScenesStore()

  const currentScene = computed(() => {
    if (!currentSceneId.value) return null
    return scenesStore.scenes.get(currentSceneId.value) || null
  })

  /**
   * Get all resolved configs for the current scene (uses store's full hierarchy resolution)
   */
  const resolvedConfigs = computed(() => {
    if (!currentSceneId.value) return []
    return sceneLightConfigsStore.getResolvedConfigsForScene(currentSceneId.value)
  })

  /**
   * Get resolved config for a specific light
   */
  const getResolvedConfig = (lightId: string): ResolvedSceneLightConfig | undefined => {
    return resolvedConfigs.value.find(c => c.light_id === lightId)
  }

  /**
   * Check if light should be in color mode based on resolved config (handles full hierarchy)
   * Lights with WLED gradient effects are always in color mode
   * Also checks current_state for real-time updates during drag
   * Drag override takes precedence when light is being actively dragged
   */
  const isLightInColorMode = (light: Light): boolean => {
    // If this light is being actively dragged, use the drag target
    if (dragOverride?.value.targetCircle && dragOverride.value.lightIds.has(light.id)) {
      return dragOverride.value.targetCircle === 'hue'
    }
    
    const config = getResolvedConfig(light.id)
    
    // If light has WLED effect settings with a gradient, it's always color mode
    // because gradient pips need to show on the hue circle
    if (config?.wled_effect_settings?.gradient?.stops && config.wled_effect_settings.gradient.stops.length >= 2) {
      return true
    }
    
    // Check current_state first for real-time updates during drag
    const state = light.current_state
    if (state) {
      // If light has CT set but no hue/sat, it's CT mode
      if (state.ct != null && state.hue == null && state.sat == null) {
        return false
      }
      // If light has hue/sat but no CT, it's color mode
      if ((state.hue != null || state.sat != null) && state.ct == null) {
        return true
      }
    }
    
    if (!config) {
      // No config at all - default to color mode
      return true
    }
    // Color mode = no color_temperature set in target_state
    return config.target_state.color_temperature == null
  }

  /**
   * Get light position based on resolved config (uses full hierarchy)
   * Uses current_state for real-time updates ONLY if it matches the expected color mode
   */
  const getLightPositionFromConfig = (light: Light): { hue?: XYCoord; white?: XYCoord } => {
    const result: { hue?: XYCoord; white?: XYCoord } = {}

    const resolvedConfig = getResolvedConfig(light.id)

    if (!resolvedConfig) {
      // No config at all - fall back to current_state position
      const state = light.current_state
      if (state?.hue != null && state?.sat != null) {
        const hsv = hueStateToHSV(state.hue, state.sat)
        result.hue = hsvToXY(hsv)
      } else if (state?.ct != null) {
        result.white = ctToXY(state.ct)
      } else {
        // No color data at all - default to center of hue circle (red, fully saturated)
        result.hue = { x: 0.5, y: 0.5 }
      }
      return result
    }

    const targetState = resolvedConfig.target_state
    const currentState = light.current_state

    // Determine expected mode from target_state
    const shouldBeInColorMode = targetState.color_temperature == null

    if (shouldBeInColorMode) {
      // Color mode expected
      // Use current_state if it has color mode data (for real-time drag updates)
      // BUT only if it doesn't have conflicting CT data (prevents race conditions)
      if (currentState?.hue != null && currentState?.sat != null && currentState.ct == null) {
        const hsv = hueStateToHSV(currentState.hue, currentState.sat)
        result.hue = hsvToXY(hsv)
      } else if (targetState.hue != null && targetState.saturation != null) {
        // Fall back to target_state from resolved config
        const hsv = hueStateToHSV(targetState.hue, targetState.saturation)
        result.hue = hsvToXY(hsv)
      } else {
        // Config exists but has no color data - default to center of hue circle
        result.hue = { x: 0.5, y: 0.5 }
      }
    } else {
      // White mode expected
      // Use current_state if it has CT data (for real-time drag updates)
      // BUT only if it doesn't have conflicting hue/sat data (prevents race conditions)
      const currentCT = currentState?.ct
      if (currentCT != null && currentState?.hue == null && currentState?.sat == null) {
        result.white = ctToXY(currentCT)
      } else if (targetState.color_temperature != null) {
        // Fall back to target_state from resolved config
        result.white = ctToXY(targetState.color_temperature)
      } else {
        // Config exists but has no CT data - default to center of white circle
        result.white = { x: 0.5, y: 0.5 }
      }
    }

    return result
  }

  /**
   * Get lights with their positions in the hue circle (color mode)
   * Returns individual lights with their positions
   */
  const lightsInHueCircle = computed((): LightWithPosition[] => {
    const results: LightWithPosition[] = []

    for (const light of lights.value) {
      // Use scene config to determine which circle
      if (!isLightInColorMode(light)) continue // Skip white mode lights

      const positions = getLightPositionFromConfig(light)
      if (!positions.hue) continue

      // Use resolved config for inheritance info (handles full hierarchy)
      const resolvedConfig = getResolvedConfig(light.id)
      const isInherited = resolvedConfig?.is_inherited ?? false
      const inheritedFrom = resolvedConfig?.inherited_from_scene_name ?? ''

      results.push({
        light,
        position: positions.hue,
        isInherited,
        inheritedFrom
      })
    }

    return results
  })

  /**
   * Get lights with their positions in the white circle (white balance mode)
   * Returns individual lights with their positions
   */
  const lightsInWhiteCircle = computed((): LightWithPosition[] => {
    const results: LightWithPosition[] = []

    for (const light of lights.value) {
      // Use scene config to determine which circle
      if (isLightInColorMode(light)) continue // Skip color mode lights

      const positions = getLightPositionFromConfig(light)
      if (!positions.white) continue

      // Use resolved config for inheritance info (handles full hierarchy)
      const resolvedConfig = getResolvedConfig(light.id)
      const isInherited = resolvedConfig?.is_inherited ?? false
      const inheritedFrom = resolvedConfig?.inherited_from_scene_name ?? ''

      results.push({
        light,
        position: positions.white,
        isInherited,
        inheritedFrom
      })
    }

    return results
  })

  /**
   * Get lights with their positions on the CCT slider (1D, 0-1 range)
   * Maps color temperature to x position: 0 = cool (6500K), 1 = warm (2000K)
   */
  const lightsInCctSlider = computed(() => {
    const results: { light: Light; position: { x: number }; isInherited: boolean; inheritedFrom: string }[] = []

    // Min/max CT in mirek (same as slider)
    const minCt = 153  // 6500K cool
    const maxCt = 500  // 2000K warm

    for (const light of lights.value) {
      if (isLightInColorMode(light)) continue // Skip color mode lights

      const config = getResolvedConfig(light.id)
      // Default to middle of range (326 mirek ~= 3100K) if no CT value set
      const ct = config?.target_state?.color_temperature ?? light.current_state?.ct ?? 326
      
      const x = (ct - minCt) / (maxCt - minCt)
      const isInherited = config?.is_inherited ?? false
      const inheritedFrom = config?.inherited_from_scene_name ?? ''

      results.push({
        light,
        position: { x: Math.max(0, Math.min(1, x)) },
        isInherited,
        inheritedFrom
      })
    }

    return results
  })

  /**
   * Calculate Euclidean distance between two points (0-1 normalized)
   */
  function distance(a: XYCoord, b: XYCoord): number {
    const dx = a.x - b.x
    const dy = a.y - b.y
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * Detect stacked groups from a list of lights with positions
   * Uses PROXIMITY_THRESHOLD to determine if lights are "stacked"
   * Lights being dragged are always rendered as singles (not part of stacks)
   */
  function detectStacks(items: LightWithPosition[]): { singles: LightWithPosition[]; stacks: StackedGroup[] } {
    const singles: LightWithPosition[] = []
    const stacks: StackedGroup[] = []
    const processed = new Set<string>()
    
    // Get the set of light IDs being dragged - these should be singles
    const draggingIds = dragOverride?.value.lightIds ?? new Set<string>()

    for (let i = 0; i < items.length; i++) {
      if (processed.has(items[i].light.id)) continue
      
      // If this light is being dragged, it's always a single
      if (draggingIds.has(items[i].light.id)) {
        singles.push(items[i])
        processed.add(items[i].light.id)
        continue
      }

      // Find all non-dragging lights within proximity of this one
      const nearby: LightWithPosition[] = [items[i]]
      processed.add(items[i].light.id)

      for (let j = i + 1; j < items.length; j++) {
        if (processed.has(items[j].light.id)) continue
        // Skip dragging lights - they are singles
        if (draggingIds.has(items[j].light.id)) continue
        
        if (distance(items[i].position, items[j].position) <= PROXIMITY_THRESHOLD) {
          nearby.push(items[j])
          processed.add(items[j].light.id)
        }
      }

      if (nearby.length === 1) {
        singles.push(nearby[0])
      } else {
        // Calculate average position for the stack
        const avgX = nearby.reduce((sum, item) => sum + item.position.x, 0) / nearby.length
        const avgY = nearby.reduce((sum, item) => sum + item.position.y, 0) / nearby.length

        stacks.push({
          id: `stack-${nearby.map(n => n.light.id).sort().join('-')}`,
          lights: nearby,
          position: { x: avgX, y: avgY }
        })
      }
    }

    return { singles, stacks }
  }

  /**
   * Combined detection for hue circle (computes once)
   */
  const hueCircleStacking = computed(() => {
    return detectStacks(lightsInHueCircle.value)
  })

  /**
   * Combined detection for white circle (computes once)
   */
  const whiteCircleStacking = computed(() => {
    return detectStacks(lightsInWhiteCircle.value)
  })

  /**
   * Get stacked groups in the hue circle
   */
  const stacksInHueCircle = computed((): StackedGroup[] => {
    return hueCircleStacking.value.stacks
  })

  /**
   * Get stacked groups in the white circle
   */
  const stacksInWhiteCircle = computed((): StackedGroup[] => {
    return whiteCircleStacking.value.stacks
  })

  /**
   * Get individual (non-stacked) lights in the hue circle
   */
  const singlesInHueCircle = computed((): LightWithPosition[] => {
    return hueCircleStacking.value.singles
  })

  /**
   * Get individual (non-stacked) lights in the white circle
   */
  const singlesInWhiteCircle = computed((): LightWithPosition[] => {
    return whiteCircleStacking.value.singles
  })

  return {
    lightsInHueCircle,
    lightsInWhiteCircle,
    lightsInCctSlider,
    stacksInHueCircle,
    stacksInWhiteCircle,
    singlesInHueCircle,
    singlesInWhiteCircle
  }
}
